import { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { usePopper } from 'react-popper';
import { useOnClickOutside } from '../../../util/use-on-click-outside';
import { OrderType } from '../../../clients/quipli-service/order/order';
import { ProductPrice, RentalProductPrice } from '../../../clients/quipli-service/pricing/pricing';
import { Product, ProductType } from '../../../clients/quipli-service/product/product';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { Typography } from '../../../components/typography/typography';
import Icon from '../../../components/icons/icon';
import { displayMoneyFormat } from '../../../util/util';
import { Location } from '../../../clients/quipli-service/location/location';
import { debounce, groupBy, partition } from 'lodash';
import { PaginationData } from '../../../clients/quipli-service/pagination/pagination';
import { useProductApiV1 } from '../../../clients/quipli-service/product/product-api-v1';
import { usePricingApiV1 } from '../../../clients/quipli-service/pricing/pricing-api-v1';

export type PriceOption = {
  first: boolean;
  isWrapper: boolean;
  productName: string;
  lowerProductName: string;
  isActive: boolean;
  object: ProductPrice;
};

export type GroupProductPrice = Pick<Product, 'id' | 'trackingType' | 'stock' | 'rentalStock' | 'saleStock'> & {
  label: string;
  options: PriceOption[];
  rentalPrices: RentalProductPrice[];
};

export const SearchProductPrice = ({
  location,
  orderId,
  userId,
  type,
  value,
  onSelect,
  onGroupSelect,
  productType = ProductType.Product,
  placeholder = 'Search product',
}: {
  location?: Location;
  orderId?: number;
  userId?: number;
  type: OrderType;
  value?: PriceOption;
  onSelect: (op: PriceOption | null) => void;
  onGroupSelect?: (op: GroupProductPrice | null) => void;
  productType?: ProductType;
  placeholder?: string;
}) => {
  const { searchProducts, listProductPricesForOrder } = useAPI();
  const { searchParts, searchServices } = useProductApiV1();
  const { listPrices } = usePricingApiV1();

  // Refs
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [controlRef, setControlRef] = useState<HTMLDivElement | null>(null);
  const [dropdownRef, setDropdownRef] = useState<HTMLDivElement | null>(null);

  // States
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isSearchLoading, setIsSearchLoading] = useState<boolean>(false);
  const [isPageLoading, setIsPageLoading] = useState<boolean>(false);
  const [wrapperWidth, setWrapperWidth] = useState<number>(0);
  const [inputValue, setInputValue] = useState<string>('');
  const [groups, setGroups] = useState<GroupProductPrice[]>([]);
  const [pagination, setPagination] = useState<PaginationData | undefined>();

  // Popper elements for dropdown
  const { styles, attributes } = usePopper(controlRef, dropdownRef, { placement: 'bottom', strategy: 'fixed' });

  // Search products
  const getProducts = useCallback(
    debounce(async (keyword: string, shouldAppend: boolean = false, productType: ProductType = ProductType.Product) => {
      if (!location) {
        setGroups([]);
        setPagination(undefined);
        setIsSearchLoading(false);
        setIsPageLoading(false);
        return;
      }

      const newGroups: GroupProductPrice[] = shouldAppend ? groups : [];
      const isForRent = type === OrderType.Rental;
      let search;
      if (productType === ProductType.Part) {
        search = await searchParts({
          keyword,
          locationIds: [location.id],
          pageSize: 10,
          pageNumber: shouldAppend ? pagination?.nextPage || 1 : 1,
          sortBy: 'name',
          sortDirection: 'asc',
        });
      } else if (productType === ProductType.Service) {
        search = await searchServices({
          keyword,
          locationIds: [location.id],
          pageSize: 10,
          pageNumber: shouldAppend ? pagination?.nextPage || 1 : 1,
          sortBy: 'name',
          sortDirection: 'asc',
        });
      } else {
        search = await searchProducts({
          keyword,
          types: [ProductType.Product],
          locationIds: [location.id],
          pageSize: 10,
          pageNumber: shouldAppend ? pagination?.nextPage || 1 : 1,
          isRental: type === OrderType.Rental ? true : undefined,
          isSale: type === OrderType.Sale ? true : undefined,
          sortBy: 'name',
          sortDirection: 'asc',
        });
      }

      const [allExistingCustomRates, allPrices] = await Promise.all([
        (orderId
          ? listProductPricesForOrder(orderId, location.id).then((res) =>
              res.filter((p) => p.type === OrderType.Rental),
            )
          : []) as RentalProductPrice[],
        listPrices({
          productIds: search.items.map((p) => p.id),
          locationId: location.id,
          userId: userId,
          type: isForRent ? OrderType.Rental : OrderType.Sale,
        }),
      ]);
      const [rp, sp] = partition(allPrices, (p) => p.type === OrderType.Rental);
      const rentPriceUnits = groupBy(rp, 'productId');
      const salePriceUnits = groupBy(sp, 'productId');
      const overriddenPriceIds = allExistingCustomRates.map((r) => r.originalPrice?.id).filter((id) => !!id);

      setGroups([
        ...newGroups,
        ...search.items
          .filter(
            (p) =>
              p.locationIds.includes(location.id) &&
              (isForRent
                ? p.allowRent &&
                  (rentPriceUnits[p.id] ?? []).some(
                    ({ locationIds }) => !locationIds || locationIds.length === 0 || locationIds.includes(location.id),
                  )
                : p.allowSale &&
                  (salePriceUnits[p.id] ?? []).some(
                    ({ locationIds }) => !locationIds || locationIds.length === 0 || locationIds.includes(location.id),
                  )),
          )
          .map((p) => {
            const productExistingCustomOrderRentalRates = allExistingCustomRates.filter((r) => r.productId === p.id);
            const prices = [
              ...productExistingCustomOrderRentalRates,
              ...((rentPriceUnits[p.id] ?? []).filter(
                (rentPrice) => rentPrice.productId === p.id && !overriddenPriceIds.includes(rentPrice.id),
              ) as RentalProductPrice[]),
            ].sort((a, b) => a.durationMinutes - b.durationMinutes);
            return {
              id: p.id,
              trackingType: p.trackingType,
              stock: p.stock,
              rentalStock: p.rentalStock,
              saleStock: p.saleStock,
              label: p.name,
              options: isForRent
                ? prices.map((price, index) => ({
                    first: index === 0,
                    isWrapper: prices.length > 1 && index === 0,
                    isActive: p.isActive,
                    productName: p.name,
                    lowerProductName: p.name.toLowerCase(),
                    object: price,
                  }))
                : (salePriceUnits[p.id] ?? []).map((price) => ({
                    first: true,
                    isWrapper: false,
                    isActive: p.isActive,
                    productName: p.name,
                    lowerProductName: p.name.toLowerCase(),
                    object: price,
                  })),
              rentalPrices: prices,
            };
          }),
      ]);
      setPagination(search.pagination);

      setIsSearchLoading(false);
      setIsPageLoading(false);
    }, 500),
    [type, location, orderId, pagination, userId],
  );

  // Scroll event to load more products
  const handleScroll = useCallback(() => {
    if (isSearchLoading || isPageLoading || !pagination?.nextPage || !dropdownRef) return;

    const { scrollTop, scrollHeight, clientHeight } = dropdownRef;

    if (scrollTop + clientHeight >= scrollHeight - 5) {
      setIsPageLoading(true);
      getProducts(inputValue, true, productType);
    }
  }, [isSearchLoading, isPageLoading, pagination, dropdownRef, inputValue, getProducts]);

  // Toggle group
  const handleToggleGroup = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, productId: number) => {
    e.stopPropagation();
    const OPTION_HEIGHT = 40;
    const ROWS = 6;
    const groupChilds = document.querySelectorAll<HTMLElement>(
      `.select-group-child[data-group-id="${productId}"]:not([data-is-wrapper="1"])`,
    );
    const groupWrapper = document.querySelector<HTMLElement>(`div[data-group-wrapper-id="${productId}"]`);

    const child = groups.find((o) => o.id === productId);
    const initialOffset =
      child && groups.indexOf(child) !== -1
        ? Array.isArray(groupChilds) && groupChilds.length >= ROWS
          ? groups.indexOf(child) >= ROWS
            ? groups.indexOf(child) * OPTION_HEIGHT - OPTION_HEIGHT * 5
            : 0
          : 0
        : 0;

    groupChilds?.forEach((child) => child.classList.toggle('hidden'));
    groupWrapper?.scrollTo(0, initialOffset);
  };

  // Close dropdown when clicked outside
  useOnClickOutside(wrapperRef, () => setIsOpen(false));

  // Resize observer to get wrapper width
  useEffect(() => {
    if (!wrapperRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      setWrapperWidth(wrapperRef.current?.scrollWidth ?? 0);
    });

    resizeObserver.observe(wrapperRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [wrapperRef]);

  // Scroll event listener
  useEffect(() => {
    if (!dropdownRef) return;

    handleScroll();

    dropdownRef.addEventListener('scroll', handleScroll);
    return () => dropdownRef.removeEventListener('scroll', handleScroll);
  }, [dropdownRef, handleScroll]);

  // Set input if there is a selected option
  useEffect(() => {
    setInputValue(value?.productName || '');
  }, [value]);

  // Get products on input change
  useEffect(() => {
    if (!location) return;

    setIsSearchLoading(true);
    getProducts(inputValue, false, productType);
  }, [inputValue, location, type, productType]);

  // Focus input on open
  useEffect(() => {
    isOpen ? inputRef.current?.focus() : inputRef.current?.blur();
  }, [isOpen]);

  useEffect(() => {
    if (userId) {
      getProducts(inputValue, false, productType);
    }
  }, [userId]);

  return (
    <div ref={wrapperRef}>
      <div
        ref={setControlRef}
        data-cy="search_products"
        className={classNames(
          'flex items-center gap-x-[6px] bg-quipli-white border border-solid border-grey-300 rounded-[0.5rem] cursor-pointer',
          {
            '!border-primary-500': isOpen,
          },
        )}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
      >
        <div className="ml-[10px] flex items-center justify-center">
          <Icon icon="Search" size="medium" stroke="var(--grey-500)" />
        </div>

        <input
          type="text"
          className="cursor-pointer !outline-none !border-0 !border-none grow text-sm"
          placeholder={placeholder}
          ref={inputRef}
          value={inputValue}
          onChange={({ target }) => setInputValue(target.value)}
        />

        {value && (
          <div
            className="flex items-center justify-center"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(null);
              onGroupSelect && onGroupSelect(null);
              setInputValue('');
            }}
          >
            <Icon icon="Close" size="medium" />
          </div>
        )}

        <div className="mr-[10px] flex items-center justify-center">
          <Icon icon={isOpen ? 'ChevronUp' : 'ChevronDown'} size="small" />
        </div>

        {isOpen && (
          <div
            ref={setDropdownRef}
            className="bg-quipli-white border border-solid border-grey-100 rounded-[9px] shadow-md mt-2 p-1 overflow-y-auto"
            style={{ ...styles.popper, zIndex: 5, width: `${wrapperWidth}px`, maxHeight: '238px' }}
            {...attributes.popper}
          >
            <ul className="list-none m-0 p-0" data-cy="product-list-wrapper">
              {isSearchLoading ? (
                <StaticItem text="Loading ..." />
              ) : groups.length === 0 ? (
                <StaticItem text="No products" />
              ) : (
                <>
                  {groups.map(({ id: productId, options }) => (
                    <li
                      key={`add-group-product-${productId}`}
                      data-group-wrapper-id={productId}
                      className="border border-grey-200 rounded-[6px]"
                    >
                      {options.map((option) => {
                        const { first, isWrapper, productName, isActive, object } = option;

                        let durationValue = null;
                        let durationUnit = null;

                        if (type === OrderType.Rental) {
                          ({ durationUnit, durationValue } = object as RentalProductPrice);
                        }

                        return (
                          <div
                            key={`add-group-product-${object.id}-option-${object.id}`}
                            className={classNames(
                              'cursor-pointer rounded-[6px] text-grey-800 py-2 px-3 hover:bg-grey-100 select-group-child',
                              {
                                hidden: !isWrapper && !first && type === OrderType.Rental,
                              },
                            )}
                            data-is-wrapper={isWrapper ? 1 : 0}
                            data-group-id={object.productId}
                            onClick={() => {
                              onSelect(option);
                              onGroupSelect && onGroupSelect(groups.find((g) => g.id === productId) || null);
                            }}
                          >
                            <div className="flex items-center justify-between">
                              {first && (
                                <Typography type="text-sm" className="text-quipli-black-800 flex items-center gap-y-10">
                                  {productName}
                                  {!isActive && <Icon icon="EyeOff" size="medium" />}
                                </Typography>
                              )}

                              <div className="flex-grow flex justify-end items-center gap-[16px]">
                                <div className="min-w-[180px]">
                                  <div
                                    className={classNames('w-full flex', {
                                      'justify-between': type === OrderType.Rental,
                                      'justify-end': type === OrderType.Sale,
                                    })}
                                  >
                                    {type === OrderType.Rental && (
                                      <Typography type="text-sm" className="text-grey-500 capitalize">{`per ${
                                        durationValue === 1 ? durationUnit : `${durationValue} ${durationUnit}s`
                                      }`}</Typography>
                                    )}

                                    <Typography type="text-sm" className="text-quipli-black-800">
                                      {type === OrderType.Rental && (object as RentalProductPrice).originalPrice ? (
                                        <>
                                          <s className="mr-1">
                                            {displayMoneyFormat(
                                              (object as RentalProductPrice).originalPrice!.price / 100,
                                              location?.currency,
                                            )}
                                          </s>
                                          {displayMoneyFormat(object.price / 100, location?.currency)}
                                        </>
                                      ) : (
                                        displayMoneyFormat(object.price / 100, location?.currency)
                                      )}
                                    </Typography>
                                  </div>
                                </div>

                                {type === OrderType.Rental && (
                                  <div
                                    className={classNames('quipli-v2 hover:bg-grey-300 px-[0.5rem] py-1 mr-2 rounded', {
                                      invisible: !isWrapper,
                                    })}
                                    onClick={(e) => isWrapper && handleToggleGroup(e, object.productId)}
                                  >
                                    <Icon icon={'ChevronDown'} size="small" />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </li>
                  ))}

                  {isPageLoading && <StaticItem text="Loading ..." />}
                </>
              )}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

const StaticItem = ({ text }: { text: string }) => (
  <li className="text-grey-800 py-2 px-3 text-center">
    <Typography type="text-sm">{text}</Typography>
  </li>
);
